import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FlowModal, ModalContent } from 'components/modal'
import { PersonalizationStudioReader } from 'components/common'
import { initXoPPDCContent, enrollPPDCXO } from '@/features/enroll-card/enroll-card-actions'
import { selectXoPPDCContent } from '@/features/enroll-card/enroll-card-slice'
import { arrangeRecommendations } from '@/shared/utils/screens/enroll-card'
import { FLOW_STEPS } from '@/constants/flow-steps'
import { signalAppEnded } from '../../initializeApp'
import { CreditAppEndStatuses } from '@paypalcorp/xo-credit-app-interface'
import { getQueryParams } from 'utils/query'
import useAnalytics from '@/hooks/useAnalytics'
import XO_PPDC_FPTI_TAGS from '@/constants/fpti-tags/xo-ppdc'
import {
  loadingSkeletonCss,
  skeletonTitleCss,
  skeletonContentCss,
  skeletonButtonContainerCss,
  skeletonButtonSmallCss,
  skeletonButtonLargeCss,
  psHeaderTextCss,
  psBodyTextCss,
  psContainerCss,
  modalOverrideCss,
} from './styles'
import { mapUcsToPsBlocks } from './mapUcsToPsBlocks'
import useModalMetadata from '@/hooks/useModalMetadata'
import ErrorPage from './ErrorPage'
import { bundleLoaded, applicationDataLoaded } from '@/appLoadedState'
import { productNames } from 'universal/cfs-card'
import { PPDCXO_CONSTANTS } from '@/constants/ppdcxo'
import { isInIframe } from '@/shared/utils/utils'
import NotFound from '../common/NotFound'
import { errorTypes } from 'universal'

const { CONSUMER_DEBIT_CARD } = productNames
const { PPDCXO_STEPUP_ENTRYPOINT, STEPUP_REQUIRED, MODAL_METADATA_ID } = PPDCXO_CONSTANTS
const { PPDC_UPSELL_XO } = FLOW_STEPS.XO_PPDC
const {
  getPpdcClickTags,
  upsellBackClickTags,
  stepupSuccessReturnActivityTags,
  stepupFailureReturnActivityTags,
  directAccessDetectedTags,
} = XO_PPDC_FPTI_TAGS

// Helper function to map API error types to user-facing error reasons
function getErrorReasonFromApiError(apiError) {
  if (!apiError?.type) return null
  
  switch (apiError.type) {
    case errorTypes.UNAUTHORIZED_ERROR:
      return 'session_expired'
    case errorTypes.REQUEST_BLOCKED_ERROR:
      return 'request_blocked'
    case errorTypes.FORBIDDEN_ERROR:
      return 'forbidden'
    case errorTypes.INTERNAL_SERVER_ERROR:
      return 'server_error'
    case errorTypes.REQUEST_CONNECTION_ERROR:
      return 'connection_error'
    case errorTypes.UNEXPECTED_SERVER_ERROR:
    case errorTypes.UNEXPECTED_RUNTIME_ERROR:
      return 'unexpected_error'
    default:
      return 'api_error'
  }
}

// ==== CHAI HANDLERS =============
const handleError = () => {
  if (window.ppdcHandlers?.signalError) {
    window.ppdcHandlers.signalError()
  } else {
    // Fallback
    signalAppEnded({ status: CreditAppEndStatuses.internalError })
  }
}

const handleCancel = () => {
  if (window.ppdcHandlers?.signalCancel) {
    window.ppdcHandlers.signalCancel()
  } else {
    // Fallback
    signalAppEnded({ status: CreditAppEndStatuses.canceled })
  }
}

const handleSuccess = () => {
  if (window.ppdcHandlers?.signalSuccess) {
    window.ppdcHandlers.signalSuccess()
  } else {
    signalAppEnded({ status: CreditAppEndStatuses.approved })
  }
}

// ================================
/* eslint-disable max-statements */
function XoPPDCUpsell() {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(true)
  const [isProvisionedError, setIsProvisionedError] = useState(false)
  const [isButtonDisabled, setIsButtonDisabled] = useState(false)
  const [isButtonLoading, setIsButtonLoading] = useState(false)
  const xoPPDCData = useSelector(selectXoPPDCContent)
  const { recommendations, isOfferValid, apiError } = xoPPDCData || {}
  const { recordClientClick, recordClientEvent } = useAnalytics()

  // Validate offer and recommendations
  const arrangedRecommendations = useMemo(
    () => (recommendations ? arrangeRecommendations(recommendations, PPDC_UPSELL_XO) : null),
    [recommendations],
  )

  const hasValidContent = useMemo(
    () =>
      isOfferValid &&
      recommendations &&
      Object.keys(recommendations).length > 0 &&
      arrangedRecommendations &&
      arrangedRecommendations.length > 0,
    [isOfferValid, recommendations, arrangedRecommendations],
  )

  useModalMetadata({ titleContent: { id: MODAL_METADATA_ID } })

  const removeParamFromQueryParams = (param) => {
    const parentUrl = new URL(window.parent.location.href)
    parentUrl.searchParams.delete(param)
    window.parent.history.replaceState({}, '', parentUrl.toString())
    const url = new URL(window.location.href)
    url.searchParams.delete(param)
    window.history.replaceState({}, '', url.toString())
  }

  // Capture query params - these values are frozen at mount time via useRef
  const queryParamsRef = useRef(getQueryParams())
  const { rs, entrypoint, failedBecause } = queryParamsRef.current
  const isReturningFromStepUpRef = useRef(entrypoint === PPDCXO_STEPUP_ENTRYPOINT)
  const isReturningFromStepUp = isReturningFromStepUpRef.current

  // Clear query params immediately to prevent FlowModal from showing denied page
  if (isReturningFromStepUp) {
    if (failedBecause) {
      removeParamFromQueryParams('failedBecause')
    }
    removeParamFromQueryParams('rs')
    removeParamFromQueryParams('entrypoint')
  }

  useEffect(() => {
    if (window.ppdcHandlers?.bundleLoaded) {
      window.ppdcHandlers.bundleLoaded()
    } else {
      bundleLoaded()
    }
  }, [])

  const handleGetCard = useCallback(async () => {
    try {
      setIsButtonDisabled(true)
      setIsButtonLoading(true)
      recordClientClick(getPpdcClickTags, {
        entry_point: PPDCXO_CONSTANTS.ENTRY_POINT,
        url_path: PPDCXO_CONSTANTS.URL_PATH,
        productName: PPDCXO_CONSTANTS.PRODUCT_NAME,
      })
      const card = await dispatch(enrollPPDCXO({ productName: CONSUMER_DEBIT_CARD }))
      if (card && card?.cardId) {
        handleSuccess()
      } else if (card && card === STEPUP_REQUIRED) {
        // Step-up is in progress, don't show error - user will be redirected
      } else {
        setIsProvisionedError(true) // Show error page
      }
    } catch (err) {
      handleError()
    } finally {
      setIsButtonLoading(false)
    }
  }, [dispatch, recordClientClick, setIsProvisionedError, setIsButtonDisabled, setIsButtonLoading])

  useEffect(() => {
    const triggerCardCreationAfterStepup = async () => {
      const hasPassedStepUp = rs === '1' && !failedBecause
      if (hasPassedStepUp) {
        await handleGetCard()
        recordClientEvent({
          ...stepupSuccessReturnActivityTags,
          entry_point: PPDCXO_CONSTANTS.ENTRY_POINT,
          url_path: PPDCXO_CONSTANTS.URL_PATH,
          productName: PPDCXO_CONSTANTS.PRODUCT_NAME,
        })
      } else {
        setIsProvisionedError(true)
        recordClientEvent({
          ...stepupFailureReturnActivityTags,
          failureReason: failedBecause,
          entry_point: PPDCXO_CONSTANTS.ENTRY_POINT,
          url_path: PPDCXO_CONSTANTS.URL_PATH,
          productName: PPDCXO_CONSTANTS.PRODUCT_NAME,
        })
      }
    }

    if (isReturningFromStepUp) {

      if (!isLoading) {
        if (hasValidContent) {
          triggerCardCreationAfterStepup()
        } else {
          handleError()
        }
      }
    }
  }, [
    dispatch,
    recordClientEvent,
    isReturningFromStepUp,
    failedBecause,
    rs,
    handleGetCard,
    isLoading,
    hasValidContent,
  ])

  useEffect(() => {
    const fetchContent = async () => {
      try {
        await dispatch(initXoPPDCContent())

        if (window.ppdcHandlers?.applicationDataLoaded) {
          window.ppdcHandlers.applicationDataLoaded()
        } else {
          applicationDataLoaded()
        }
      } catch (error) {
        setIsProvisionedError(true)

        if (window.ppdcHandlers?.applicationDataLoaded) {
          window.ppdcHandlers.applicationDataLoaded()
        } else {
          applicationDataLoaded()
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchContent()
  }, [dispatch])

  const handleBack = () => {
    recordClientClick(upsellBackClickTags, {
      entry_point: PPDCXO_CONSTANTS.ENTRY_POINT,
      url_path: PPDCXO_CONSTANTS.URL_PATH,
      productName: PPDCXO_CONSTANTS.PRODUCT_NAME,
    })
    handleCancel()
  }

  if (!isInIframe()) {
    recordClientEvent({
      ...directAccessDetectedTags, ...{
        entry_point: PPDCXO_CONSTANTS.ENTRY_POINT,
        url_path: PPDCXO_CONSTANTS.URL_PATH,
        productName: PPDCXO_CONSTANTS.PRODUCT_NAME
      }
    })

    return < NotFound />
  }

  if (isLoading) {
    return (
      <FlowModal
        flowName={PPDC_UPSELL_XO}
        modalOverrideCss={modalOverrideCss}
        onFirstStepBack={handleBack}
      >
        <div
          css={loadingSkeletonCss}
          role="status"
          aria-live="polite"
          aria-label="Loading PayPal Debit Card information"
        >
          <div className="loading-skeleton">
            <div css={skeletonTitleCss} aria-hidden="true" />
            <div css={skeletonContentCss} aria-hidden="true" />
            <div css={skeletonButtonContainerCss} aria-hidden="true">
              <div css={skeletonButtonSmallCss} />
              <div css={skeletonButtonLargeCss} />
            </div>
          </div>
        </div>
      </FlowModal>
    )
  }

  // Check API errors first using helper function
  let errorReason = getErrorReasonFromApiError(apiError)

  // If no API error, check business logic conditions
  if (!errorReason) {
    if (isOfferValid === false) {
      errorReason = 'invalid_offer'
    } else if (isProvisionedError) {
      errorReason = 'provisioning_failed'
    } else if (!hasValidContent) {
      errorReason = 'no_valid_content'
    }
  }

  if (errorReason) {
    return (
      <FlowModal flowName={PPDC_UPSELL_XO} modalOverrideCss={modalOverrideCss}>
        <ErrorPage reason={errorReason} />
      </FlowModal>
    )
  }

  const options = {
    onButtonClick: handleGetCard,
    disabled: isButtonDisabled,
    loading: isButtonLoading,
  }

  const cssProps = {
    headerTextCss: psHeaderTextCss,
    bodyTextCss: psBodyTextCss,
  }

  const psBlocks = mapUcsToPsBlocks(arrangedRecommendations)

  return (
    <FlowModal
      flowName={PPDC_UPSELL_XO}
      modalOverrideCss={modalOverrideCss}
      onFirstStepBack={!isProvisionedError ? handleBack : undefined}
    >
      <ModalContent data-testid="xo-ppdc-value-prop-page">
        <div css={psContainerCss}>
          <PersonalizationStudioReader
            arrangedRecommendations={psBlocks}
            options={options}
            name="xo-ppdc-upsell"
            cssProps={cssProps}
          />
        </div>
      </ModalContent>
    </FlowModal>
  )
}

export default XoPPDCUpsell
